import { useEffect, useState } from 'react';
import { useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

type Contact = { id: string; tags: string[]; suppressedReason: string | null; unsubscribedAt: string | null };

export function ContactActions({ contact }: { contact: Contact }) {
  const { revalidate } = useRevalidator();
  const [tag, setTag] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [templates, setTemplates] = useState<{ id: string; name: string; kind: string }[]>([]);
  const [templateId, setTemplateId] = useState('');
  useEffect(() => {
    api<{ id: string; name: string; kind: string }[]>('/templates').then(setTemplates).catch(() => setTemplates([]));
  }, []);

  async function run(name: string, fn: () => Promise<unknown>, okText: string) {
    setBusy(name);
    setMessage(null);
    try {
      await fn();
      setMessage({ kind: 'ok', text: okText });
      revalidate();
    } catch (err) {
      setMessage({ kind: 'error', text: errMsg(err, 'Failed') });
    } finally {
      setBusy(null);
    }
  }

  const unsubscribed = !!contact.suppressedReason;
  const excluded = contact.suppressedReason === 'excluded';

  return (
    <aside className="space-y-5 rounded-lg border bg-white p-4 text-sm">
      <section className="space-y-2">
        <h3 className="text-base">Send email</h3>
        <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className="h-9 w-full rounded-md border px-2">
          <option value="">— pick a template —</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.kind})
            </option>
          ))}
        </select>
        <Button
          className="w-full"
          disabled={busy !== null || !templateId}
          onClick={() =>
            run(
              'send',
              async () => {
                const t = templates.find((x) => x.id === templateId);
                const r = await api<{ status: string; reason?: string; send?: { sandbox: boolean } }>(`/contacts/${contact.id}/send`, {
                  method: 'POST',
                  json: { templateId, domain: t?.kind === 'transactional' ? 'transactional' : 'marketing' },
                });
                if (r.status !== 'sent') throw new Error(`Not sent: ${r.reason}`);
                return r;
              },
              'Email sent (check SEND_MODE — sandbox rewrites the recipient)',
            )
          }
        >
          Send now
        </Button>
      </section>

      <section className="space-y-2">
        <h3 className="text-base">Tags</h3>
        <div className="flex flex-wrap gap-1">
          {contact.tags.map((t) => (
            <button
              key={t}
              className="rounded bg-muted px-1.5 py-0.5 text-xs hover:bg-red-50 hover:text-red-700"
              title="Remove tag"
              disabled={busy !== null}
              onClick={() => run('tag', () => api(`/contacts/${contact.id}/tags/${encodeURIComponent(t)}`, { method: 'DELETE' }), `Removed #${t}`)}
            >
              #{t} ×
            </button>
          ))}
        </div>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!tag.trim()) return;
            run('tag', () => api(`/contacts/${contact.id}/tags`, { method: 'POST', json: { tag: tag.trim() } }), `Added #${tag.trim()}`).then(() => setTag(''));
          }}
        >
          <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="new tag" className="h-9 flex-1 rounded-md border px-2" />
          <Button type="submit" size="sm" disabled={busy !== null}>
            Add
          </Button>
        </form>
      </section>

      <section className="space-y-2">
        <h3 className="text-base">Note</h3>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full rounded-md border p-2" placeholder="Becomes an audit-log entry" />
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null || !note.trim()}
          onClick={() => run('note', () => api(`/contacts/${contact.id}/notes`, { method: 'POST', json: { text: note.trim() } }), 'Note added').then(() => setNote(''))}
        >
          Add note
        </Button>
      </section>

      <section className="space-y-2">
        <h3 className="text-base">Marketing emails</h3>
        <p className="text-xs text-muted-foreground">
          {excluded
            ? 'On the staff/test exclusion list — cannot be re-subscribed.'
            : unsubscribed
              ? `Suppressed (${contact.suppressedReason}).`
              : 'Eligible for marketing email.'}
        </p>
        <Button
          size="sm"
          variant={unsubscribed ? 'outline' : 'destructive'}
          disabled={busy !== null || excluded}
          onClick={() => run('unsub', () => api(`/contacts/${contact.id}/unsubscribe`, { method: 'POST', json: { unsubscribed: !unsubscribed } }), unsubscribed ? 'Re-subscribed' : 'Unsubscribed')}
        >
          {unsubscribed ? 'Re-subscribe' : 'Unsubscribe'}
        </Button>
      </section>

      {message && (
        <p role="status" className={`rounded-md p-2 text-xs ${message.kind === 'ok' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
          {message.text}
        </p>
      )}
    </aside>
  );
}
